import { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Separator } from '@/components/ui/separator';
import { useTvControllers } from '@/lib/api/tvController';
import {
  saveTvControllerGroups,
  saveTvControllerGroupsWithTags,
  TagChange,
  useTvControllerGroups,
} from '@/lib/api/tvControllerGroup';
import { TvController } from '@/lib/schema/tvController';
import {
  flattenGroups,
  insertGroup,
  MAX_GROUP_DEPTH,
  removeGroup,
  shiftGroup,
  TvControllerGroup,
  updateGroup,
} from '@/lib/schema/tvControllerGroup';
import { ArrowDown, ArrowUp, MoreHorizontal, QrCode } from 'lucide-react';
import { toast } from 'sonner';
import { v4 as uuidv4 } from 'uuid';
import { ungroupedTags } from '../groupTree';
import { TvControllersTabs } from '../tvControllersTabs';
import { GroupMembersDialog } from './groupMembersDialog';
import { GroupNameDialog } from './groupNameDialog';
import { GroupQrDialog } from './groupQrDialog';
import { GroupChannelGuideDialog } from './groupChannelGuideDialog';
import { useChannelGuides } from '@/lib/api/channelGuide';
import { pushGuide } from '@/lib/api/guideSync';
import {
  groupTvs,
  guidesByTag,
  guideStatus,
  parentGroupOf,
} from '@/lib/schema/guideSync';
import { MoveGroupDialog } from './moveGroupDialog';

type NameDialogState =
  // tag = adopt an existing ungrouped tag (and its TVs) as the new group
  | { mode: 'create'; parentId: string | null; tag?: string }
  | { mode: 'rename'; group: TvControllerGroup };

export default function TvControllerGroups() {
  const [values] = useTvControllers();
  const [groups, loading] = useTvControllerGroups();

  const [nameDialog, setNameDialog] = useState<NameDialogState | null>(null);
  const [membersOf, setMembersOf] = useState<TvControllerGroup | null>(null);
  const [groupToMove, setGroupToMove] = useState<TvControllerGroup | null>(
    null
  );
  const [qrOf, setQrOf] = useState<TvControllerGroup | null>(null);
  const [guideOf, setGuideOf] = useState<TvControllerGroup | null>(null);
  const [channelGuides] = useChannelGuides();
  const guideNames = useMemo(
    () =>
      new Map(
        ((channelGuides || []) as { id: string; name: string }[]).map(
          (guide) => [guide.id, guide.name]
        )
      ),
    [channelGuides]
  );
  const guidesById = useMemo(
    () =>
      new Map(
        ((channelGuides || []) as { id: string; hash?: string }[]).map(
          (guide) => [guide.id, guide]
        )
      ),
    [channelGuides]
  );
  const [groupToDelete, setGroupToDelete] = useState<TvControllerGroup | null>(
    null
  );
  const [isDeleting, setIsDeleting] = useState(false);

  const tvControllers = useMemo(
    () => (values || []) as TvController[],
    [values]
  );

  const idsByTag = useMemo(() => {
    const map = new Map<string, string[]>();
    tvControllers.forEach((tv) => {
      (tv.tags || []).forEach((tag) => {
        if (tv.id) map.set(tag, [...(map.get(tag) || []), tv.id]);
      });
    });
    return map;
  }, [tvControllers]);

  // TVs of each parent group that do not hold the current version of its guide
  const behindByGroup = useMemo(() => {
    const byTag = guidesByTag(groups);
    const map = new Map<string, number>();
    groups.forEach((group) => {
      if (!group.channelGuideId) return;
      map.set(
        group.id,
        groupTvs(group, tvControllers).filter((tv) => {
          const { state } = guideStatus(tv, byTag, guidesById);
          return state === 'outdated' || state === 'unknown';
        }).length
      );
    });
    return map;
  }, [groups, tvControllers, guidesById]);

  const reloadGuide = (group: TvControllerGroup) => {
    if (!group.channelGuideId) return;
    pushGuide(
      group.channelGuideId,
      groupTvs(group, tvControllers).map((tv) => tv.ip),
      `Guide for ${group.name}`
    );
  };

  const ungrouped = useMemo(
    () => ungroupedTags(groups, tvControllers),
    [groups, tvControllers]
  );

  const takenNames = useMemo(() => {
    const renamingId = nameDialog?.mode === 'rename' ? nameDialog.group.id : '';
    return new Set(
      flattenGroups(groups)
        .filter((group) => group.id !== renamingId)
        .map((group) => group.name.trim().toLowerCase())
    );
  }, [groups, nameDialog]);

  // Toast the failure and rethrow so the calling dialog stays open.
  const save = async (
    nodes: TvControllerGroup[],
    success: string,
    changes?: TagChange[]
  ) => {
    try {
      if (changes?.length) await saveTvControllerGroupsWithTags(nodes, changes);
      else await saveTvControllerGroups(nodes);
      toast.success(success);
    } catch (error) {
      console.error('Error saving ZeeVee groups:', error);
      toast.error('Failed to save groups');
      throw error;
    }
  };

  const handleNameSubmit = async (name: string) => {
    if (!nameDialog) return;
    if (nameDialog.mode === 'rename') {
      await save(
        updateGroup(groups, nameDialog.group.id, (group) => ({
          ...group,
          name,
        })),
        'Group renamed'
      );
      return;
    }
    await save(
      insertGroup(groups, nameDialog.parentId, {
        id: uuidv4(),
        name,
        tag: nameDialog.tag ?? null,
        children: [],
      }),
      'Group created'
    );
  };

  const handleMembersSubmit = async (
    group: TvControllerGroup,
    memberIds: string[]
  ) => {
    // First TVs added to a group: stamp them with a tag named after it.
    let tag = group.tag;
    if (!tag) {
      const inUse = new Set([
        ...idsByTag.keys(),
        ...flattenGroups(groups).map((g) => g.tag),
      ]);
      tag = group.name;
      for (let n = 2; inUse.has(tag); n++) tag = `${group.name}_${n}`;
    }
    const current = idsByTag.get(tag) || [];
    const added = memberIds.filter((id) => !current.includes(id));
    await save(
      updateGroup(groups, group.id, (g) => ({
        ...g,
        tag: memberIds.length ? tag : null,
      })),
      'Group updated',
      [
        {
          tag,
          addIds: added,
          removeIds: current.filter((id) => !memberIds.includes(id)),
        },
      ]
    );
    // TVs joining a group get the guide of its parent group right away.
    const parent = parentGroupOf(groups, group.id);
    if (parent?.channelGuideId && added.length) {
      pushGuide(
        parent.channelGuideId,
        tvControllers.filter((tv) => added.includes(tv.id!)).map((tv) => tv.ip),
        `Guide for ${parent.name}`
      );
    }
  };

  const handleMove = async (
    group: TvControllerGroup,
    parentId: string | null
  ) => {
    const moved = insertGroup(removeGroup(groups, group.id), parentId, group);
    await save(moved, 'Group moved');
    // TVs moved under another parent group take that group's guide.
    const before = parentGroupOf(groups, group.id);
    const after = parentGroupOf(moved, group.id);
    if (
      after?.channelGuideId &&
      after.channelGuideId !== before?.channelGuideId
    ) {
      pushGuide(
        after.channelGuideId,
        groupTvs(group, tvControllers).map((tv) => tv.ip),
        `Guide for ${after.name}`
      );
    }
  };

  // One guide per parent group: store it on the node, then load it on every TV
  // of the group and its subgroups so the assignment and the TVs agree.
  const handleAssignGuide = async (
    group: TvControllerGroup,
    channelGuideId: string
  ) => {
    await save(
      updateGroup(groups, group.id, (g) => ({ ...g, channelGuideId })),
      'Channel guide assigned'
    );
    pushGuide(
      channelGuideId,
      groupTvs(group, tvControllers).map((tv) => tv.ip),
      `Guide for ${group.name}`
    );
  };

  const handleShift = (group: TvControllerGroup, direction: -1 | 1) => {
    save(shiftGroup(groups, group.id, direction), 'Group reordered').catch(
      () => undefined
    );
  };

  // TVs lose membership of the deleted group and of every subgroup under it.
  const deleteTags = groupToDelete
    ? flattenGroups([groupToDelete]).flatMap((group) => group.tag ?? [])
    : [];
  const deleteTvCount = new Set(
    deleteTags.flatMap((tag) => idsByTag.get(tag) || [])
  ).size;

  const handleDelete = async () => {
    if (!groupToDelete) return;
    setIsDeleting(true);
    try {
      await save(
        removeGroup(groups, groupToDelete.id),
        'Group deleted',
        deleteTags.map((tag) => ({ tag, removeIds: idsByTag.get(tag) || [] }))
      );
      setGroupToDelete(null);
    } catch {
      // Already toasted by save(); keep the dialog open.
    } finally {
      setIsDeleting(false);
    }
  };

  const renderGroup = (
    group: TvControllerGroup,
    depth: number,
    index: number,
    siblings: number
  ): React.ReactNode => (
    <div key={group.id}>
      <div
        className="flex items-center gap-3 border-b py-1.5 pr-2 hover:bg-muted/50"
        style={{ paddingLeft: 12 + (depth - 1) * 24 }}
      >
        <span className={depth === 1 ? 'font-medium' : 'text-sm'}>
          {group.name}
        </span>
        <span className="text-xs text-muted-foreground">
          {group.tag
            ? `${idsByTag.get(group.tag)?.length || 0} TV(s)`
            : group.children.length
              ? `${group.children.length} subgroup(s)`
              : 'empty'}
        </span>
        {depth === 1 && (
          <span
            className={
              group.channelGuideId
                ? 'text-xs text-muted-foreground'
                : 'text-xs text-amber-600'
            }
          >
            {group.channelGuideId
              ? `Guide: ${guideNames.get(group.channelGuideId) ?? 'unknown'}`
              : 'no channel guide'}
          </span>
        )}
        {depth === 1 && !!behindByGroup.get(group.id) && (
          <span className="text-xs text-amber-600">
            {behindByGroup.get(group.id)} TV(s) out of date
          </span>
        )}
        <span className="ml-auto font-mono text-xs text-muted-foreground">
          {depth === 1 ? group.code : ''}
        </span>
        {depth === 1 ? (
          <Button
            variant="ghost"
            className="h-8 w-8 p-0"
            onClick={() => setQrOf(group)}
          >
            <span className="sr-only">Show QR code</span>
            <QrCode className="h-4 w-4" />
          </Button>
        ) : (
          <span className="h-8 w-8" />
        )}
        <Button
          variant="ghost"
          className="h-8 w-8 p-0"
          disabled={index === 0}
          onClick={() => handleShift(group, -1)}
        >
          <span className="sr-only">Move up</span>
          <ArrowUp className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          className="h-8 w-8 p-0"
          disabled={index === siblings - 1}
          onClick={() => handleShift(group, 1)}
        >
          <span className="sr-only">Move down</span>
          <ArrowDown className="h-4 w-4" />
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-8 w-8 p-0">
              <span className="sr-only">Open menu</span>
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => setMembersOf(group)}>
              Edit TVs
            </DropdownMenuItem>
            {depth === 1 && (
              <DropdownMenuItem onClick={() => setGuideOf(group)}>
                Assign channel guide ...
              </DropdownMenuItem>
            )}
            {depth === 1 && (
              <DropdownMenuItem
                disabled={!group.channelGuideId}
                onClick={() => reloadGuide(group)}
              >
                Reload guide
              </DropdownMenuItem>
            )}
            <DropdownMenuItem
              disabled={depth >= MAX_GROUP_DEPTH}
              onClick={() =>
                setNameDialog({ mode: 'create', parentId: group.id })
              }
            >
              Add subgroup
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => setNameDialog({ mode: 'rename', group })}
            >
              Rename
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setGroupToMove(group)}>
              Move to ...
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="text-destructive"
              onClick={() => setGroupToDelete(group)}
            >
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      {group.children.map((child, childIndex) =>
        renderGroup(child, depth + 1, childIndex, group.children.length)
      )}
    </div>
  );

  const parentName =
    nameDialog?.mode === 'create' && nameDialog.parentId
      ? flattenGroups(groups).find((g) => g.id === nameDialog.parentId)?.name
      : undefined;

  return (
    <div className="w-full pb-4">
      <div className="flex justify-between items-center">
        <div className="flex flex-col gap-4">
          <h1 className="text-3xl font-bold">ZeeVee Dashboard</h1>
          <TvControllersTabs value="groups" />
        </div>
        <Button
          type="button"
          onClick={() => setNameDialog({ mode: 'create', parentId: null })}
        >
          New group
        </Button>
      </div>

      <Separator className="my-6" />

      <div className="rounded-md border border-b-0">
        {groups.length ? (
          groups.map((group, index) =>
            renderGroup(group, 1, index, groups.length)
          )
        ) : (
          <p className="border-b p-6 text-center text-sm text-muted-foreground">
            {loading ? 'Loading ...' : 'No groups yet. Create one to start.'}
          </p>
        )}
      </div>

      {ungrouped.length > 0 && (
        <div className="mt-8">
          <h2 className="text-lg font-semibold">Ungrouped tags</h2>
          <p className="pb-3 text-sm text-muted-foreground">
            Tags found on TVs that no group uses yet.
          </p>
          <div className="rounded-md border border-b-0">
            {ungrouped.map((tag) => (
              <div
                key={tag}
                className="flex items-center gap-3 border-b py-1.5 pl-3 pr-2"
              >
                <span className="text-sm">{tag}</span>
                <span className="text-xs text-muted-foreground">
                  {idsByTag.get(tag)?.length || 0} TV(s)
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  className="ml-auto"
                  onClick={() =>
                    setNameDialog({ mode: 'create', parentId: null, tag })
                  }
                >
                  Add as group
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      <GroupNameDialog
        open={!!nameDialog}
        onOpenChange={(open) => {
          if (!open) setNameDialog(null);
        }}
        title={
          nameDialog?.mode === 'rename'
            ? 'Rename group'
            : parentName
              ? `New subgroup in "${parentName}"`
              : 'New group'
        }
        description={
          nameDialog?.mode === 'rename'
            ? 'Renaming changes the QR code.'
            : undefined
        }
        initialName={
          nameDialog?.mode === 'rename'
            ? nameDialog.group.name
            : nameDialog?.tag || ''
        }
        takenNames={takenNames}
        submitLabel={nameDialog?.mode === 'rename' ? 'Rename' : 'Create'}
        onSubmit={handleNameSubmit}
      />

      <GroupMembersDialog
        group={membersOf}
        onOpenChange={(open) => {
          if (!open) setMembersOf(null);
        }}
        tvControllers={tvControllers}
        onSubmit={handleMembersSubmit}
      />

      <MoveGroupDialog
        group={groupToMove}
        groups={groups}
        onOpenChange={(open) => {
          if (!open) setGroupToMove(null);
        }}
        onSubmit={handleMove}
      />

      <GroupChannelGuideDialog
        group={guideOf}
        channelGuides={(channelGuides || []) as { id: string; name: string }[]}
        onOpenChange={(open) => {
          if (!open) setGuideOf(null);
        }}
        onSubmit={handleAssignGuide}
      />

      <GroupQrDialog
        group={qrOf && (groups.find((g) => g.id === qrOf.id) ?? qrOf)}
        onOpenChange={(open) => {
          if (!open) setQrOf(null);
        }}
      />

      <Dialog
        open={!!groupToDelete}
        onOpenChange={(open) => {
          if (!open) setGroupToDelete(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete group</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{groupToDelete?.name}"
              {groupToDelete?.children.length ? ' and its subgroups' : ''}?{' '}
              {deleteTvCount} TV(s) will be removed from{' '}
              {groupToDelete?.children.length ? 'them' : 'it'}. The TVs
              themselves are not deleted.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setGroupToDelete(null)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={handleDelete}
              disabled={isDeleting}
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
